import { useEffect, useMemo, useState } from "react";
import type { CardDef, ChoiceResponse, GameView, Resource, ResourceLocation, ResourcePlan } from "../types";
import { RESOURCES, resourceMeta } from "../gameMeta";
import { GameCard } from "./Card";
import { DeckDirectionGuide, deckPositionLabel } from "./DeckDirection";
import { ResourceLocationPicker, allocateResourceSlots } from "./ResourcePlanner";
import { ResourceAmounts, ResourceBadge, ResourceCountSelector, ResourceIcon } from "./ResourceIcon";

interface Props {
  game: GameView;
  definitions: Map<string, CardDef>;
  busy: boolean;
  suspended?: boolean;
  onResolve: (response: ChoiceResponse, plan?: ResourcePlan) => void;
}

export function ChoiceModal({ game, definitions, busy, suspended = false, onResolve }: Props) {
  const choice = game.pending_choice;
  const [resources, setResources] = useState<Resource[]>([]);
  const [cards, setCards] = useState<number[]>([]);
  const [destinations, setDestinations] = useState<Record<number, number>>({});
  const [deck, setDeck] = useState<number>();
  const [payment, setPayment] = useState<Array<ResourceLocation | null>>([]);
  const [storage, setStorage] = useState<Array<ResourceLocation | null>>([]);
  const [minimized, setMinimized] = useState(false);
  const chooserSeat = choice?.seat || game.viewer;
  const chooser = game.players[chooserSeat === "rood" ? 0 : 1];
  useEffect(() => {
    setResources([]);
    setCards([]);
    setDestinations({});
    setDeck(undefined);
    setPayment([]);
    setStorage([]);
    setMinimized(false);
  }, [choice?.choice_id]);
  useEffect(() => {
    if (choice?.kind.kind === "resource_locations") {
      const slots = choice.kind.single_location
        ? Array<ResourceLocation | null>(choice.kind.resources.length).fill(null)
        : allocateResourceSlots(chooser, choice.kind.resources, choice.kind.mode);
      setPayment(choice.kind.mode === "payment" ? slots : []);
      setStorage(choice.kind.mode === "storage" ? slots : []);
      return;
    }
    const chooseTransferTypes = choice?.title === "Handelsvoordeel";
    setStorage(chooseTransferTypes ? [] : allocateResourceSlots(chooser, resources, "storage"));
    setPayment(choice?.title === "Reizende koopman"
      ? allocateResourceSlots(chooser, Array(resources.length).fill("goud" as Resource), "payment")
      : []);
  // A same-version WebSocket refresh must not erase a choice the player is
  // currently making. A new choice always receives a fresh choice_id.
  }, [choice?.choice_id, resources]);

  const revealed = useMemo(
    () => new Map(choice?.revealed_cards.map((card) => [card.instance_id, card]) || []),
    [choice?.revealed_cards],
  );
  if (!choice || suspended) return null;
  if (!choice.is_mine) {
    return (
      <div className="waiting-choice" role="status">
        <span className="spinner" />
        <div>
          <strong>{choice.title}</strong>
          <p>{game.players[choice.seat === "rood" ? 0 : 1].name} maakt een keuze…</p>
        </div>
      </div>
    );
  }

  const kind = choice.kind;
  const searchableDecks = kind.kind === "cards"
    ? game.supply.decks.filter((item) => item.searchable_cards?.some((card) => kind.options.includes(card.instance_id)))
    : [];
  const resourceChoice = kind.kind === "resources" || kind.kind === "same_resources";
  const chooseTransferTypes = choice.title === "Handelsvoordeel";
  const usesResourceBoard = kind.kind === "resource_locations"
    || kind.kind === "pay_gold_or_remove_unit"
    || (resourceChoice && !chooseTransferTypes);
  const paymentComplete = choice.title !== "Reizende koopman" || payment.length === resources.length && payment.every(Boolean);
  const addResource = (resource: Resource, limit: number, sameType: boolean) => {
    if (sameType && resources.length && resources[0] !== resource) {
      setResources([resource]);
    } else if (resources.length < limit) {
      setResources([...resources, resource]);
    }
  };
  const removeResource = (resource: Resource) => {
    const index = resources.lastIndexOf(resource);
    if (index >= 0) setResources(resources.filter((_, item) => item !== index));
  };
  const toggleCard = (id: number, limit: number) => {
    setCards((current) => current.includes(id)
      ? current.filter((card) => card !== id)
      : current.length < limit ? [...current, id] : current);
    setDestinations((current) => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  };

  if (minimized) {
    return (
      <div className="modal-layer priority-modal choice-peek-layer">
        <section className="choice-peek-bar" role="dialog" aria-modal="false" aria-labelledby="choice-peek-title">
          <div>
            <span className="eyebrow">Keuze wacht</span>
            <strong id="choice-peek-title">{choice.title}</strong>
            <small>Bekijk rustig je bord en hand; je selectie blijft bewaard.</small>
          </div>
          <button className="primary" type="button" onClick={() => setMinimized(false)}>Keuze openen</button>
        </section>
      </div>
    );
  }

  const selectedDeck = game.supply.decks.find((item) => item.deck_id === deck);
  const canSubmit =
    kind.kind === "resources"
      ? (kind.allow_fewer || resources.length === kind.count) && resources.length <= kind.count && paymentComplete
      : kind.kind === "same_resources"
        ? resources.length === kind.count
      : kind.kind === "cards" || kind.kind === "card_target"
        ? (kind.allow_fewer || cards.length === kind.count) && cards.length <= kind.count
        : kind.kind === "cards_to_deck"
          ? (kind.allow_fewer || cards.length === kind.count) && cards.length <= kind.count && cards.every((id) => destinations[id])
        : kind.kind === "starting_deck"
          ? deck !== undefined && (!kind.search || cards.length === 3)
          : kind.kind === "draw_deck"
            ? deck !== undefined
            : kind.kind === "landscape_cards"
              ? cards.length === kind.count
              : kind.kind === "resource_locations"
                ? (kind.mode === "payment" ? payment : storage).length === kind.resources.length
                  && (kind.mode === "payment" ? payment : storage).every(Boolean)
              : false;
  const selectedLandscapes = kind.kind === "landscape_cards"
    ? cards.map((id) => kind.options.find((region) => region.card_id === id)).filter(Boolean)
    : [];

  return (
    <div className={`modal-layer priority-modal board-interaction-layer choice-review-layer ${usesResourceBoard ? "board-safe-dock-layer resource-board-dock-layer" : ""}`}>
      <section className={`modal choice-modal board-interaction-dock choice-review-dock ${usesResourceBoard ? "board-safe-dock resource-board-dock resource-choice-dock" : ""} ${searchableDecks.length ? "stack-search-dock" : ""}`} role="dialog" aria-modal="false" aria-labelledby="choice-title">
        <header className="choice-review-titlebar">
          <div>
            <span className="eyebrow">Verplichte keuze</span>
            <h2 id="choice-title">{choice.title}</h2>
          </div>
          <button type="button" onClick={() => setMinimized(true)}>Bord en hand bekijken</button>
        </header>
        <p>{choice.description}</p>
        <p className="choice-context-hint">Het bord blijft zichtbaar en de handkolom rechts kan gewoon worden bekeken en gescrold.</p>

        {kind.kind === "resources" || kind.kind === "same_resources" ? (
          <ResourceCountSelector
            className="resource-picker"
            selected={resources}
            count={kind.count}
            allowed={kind.allowed.length ? kind.allowed : RESOURCES}
            canAdd={(resource) => resources.length < kind.count
              || (kind.kind === "same_resources" && resources.length > 0 && resources[0] !== resource)}
            onAdd={(resource) => addResource(resource, kind.count, kind.kind === "same_resources")}
            onRemove={removeResource}
            onClear={() => setResources([])}
          />
        ) : null}

        {resourceChoice && resources.length && !chooseTransferTypes ? (
          <ResourceLocationPicker
            player={chooser}
            resources={resources}
            mode="storage"
            slots={storage}
            onChange={setStorage}
            allowUnstored
            autoActivate={choice.title !== "Reizende koopman"}
          />
        ) : null}

        {kind.kind === "resource_locations" ? (
          <ResourceLocationPicker
            player={chooser}
            resources={kind.resources}
            mode={kind.mode}
            slots={kind.mode === "payment" ? payment : storage}
            onChange={kind.mode === "payment" ? setPayment : setStorage}
            singleLocation={kind.single_location}
            autoActivate
          />
        ) : null}

        {choice.title === "Reizende koopman" && resources.length ? (
          <ResourceLocationPicker
            player={chooser}
            resources={Array(resources.length).fill("goud" as Resource)}
            mode="payment"
            slots={payment}
            onChange={setPayment}
            autoActivate
          />
        ) : null}

        {kind.kind === "cards" || kind.kind === "card_target" ? (
          kind.kind === "cards" && searchableDecks.length ? (
            <div className="searched-deck-list" aria-label="Doorzochte trekstapels">
              <DeckDirectionGuide />
              {searchableDecks.map((item) => (
                <OrderedDeckCards
                  key={item.deck_id}
                  deck={item}
                  definitions={definitions}
                  selected={cards}
                  onToggle={(id) => toggleCard(id, kind.count)}
                />
              ))}
            </div>
          ) : (
            <div className="card-picker">
              {kind.options.map((id) => {
                const instance = revealed.get(id);
                return instance ? (
                  <GameCard
                    key={id}
                    instance={instance}
                    definition={definitions.get(instance.card_id)}
                    selected={cards.includes(id)}
                    onClick={() => toggleCard(id, kind.count)}
                  />
                ) : (
                  <button className={`unknown-card-choice ${cards.includes(id) ? "selected" : ""}`} key={id} onClick={() => toggleCard(id, kind.count)}>
                    Kaart #{id}
                  </button>
                );
              })}
            </div>
          )
        ) : null}

        {kind.kind === "cards_to_deck" ? (
          <>
            <DeckDirectionGuide compact />
            <div className="card-picker deck-placement-picker">
              {kind.options.map((id) => {
                const instance = revealed.get(id);
                const definition = instance ? definitions.get(instance.card_id) : undefined;
                const decks = game.supply.decks.filter((item) => item.set === definition?.set);
                return instance ? (
                  <div className="placement-choice" key={id}>
                    <GameCard
                      instance={instance}
                      definition={definition}
                      selected={cards.includes(id)}
                      onClick={() => toggleCard(id, kind.count)}
                    />
                    {cards.includes(id) ? (
                      <select
                        aria-label={`Trekstapel voor ${definition?.name || id}`}
                        value={destinations[id] || ""}
                        onChange={(event) => setDestinations({ ...destinations, [id]: Number(event.target.value) })}
                      >
                        <option value="">Leg onderop…</option>
                        {decks.map((deck) => <option key={deck.deck_id} value={deck.deck_id}>{deck.label} · onderop</option>)}
                      </select>
                    ) : null}
                  </div>
                ) : null;
              })}
            </div>
          </>
        ) : null}

        {kind.kind === "starting_deck" || kind.kind === "draw_deck" ? (
          <>
            <DeckDirectionGuide compact />
            <div className="deck-picker">
              {game.supply.decks
                .filter((item) => kind.deck_ids.includes(item.deck_id))
                .map((item) => (
                  <button
                    type="button"
                    className={deck === item.deck_id ? "selected" : ""}
                    key={item.deck_id}
                    onClick={() => {
                      setDeck(deck === item.deck_id ? undefined : item.deck_id);
                      setCards([]);
                    }}
                  >
                    <span>Stapel {item.deck_id}</span>
                    <strong>{item.label}</strong>
                    <small>{item.count} kaarten · trek bovenop</small>
                  </button>
                ))}
            </div>
            {kind.kind === "starting_deck" && kind.search && selectedDeck?.searchable_cards ? (
              <OrderedDeckCards
                deck={selectedDeck}
                definitions={definitions}
                selected={cards}
                onToggle={(id) => toggleCard(id, 3)}
                compact
              />
            ) : null}
          </>
        ) : null}

        {kind.kind === "landscape_cards" ? (
          <>
            <section className="scout-placement-preview" aria-label="Plaatsing van de Verkenner-landschappen">
              <div className={`scout-placement-slot ${selectedLandscapes[0] ? "filled" : ""}`}>
                <span>Boven het nieuwe dorp</span>
                {selectedLandscapes[0] ? (
                  <strong><ResourceIcon resource={selectedLandscapes[0]!.resource} /> {resourceMeta[selectedLandscapes[0]!.resource].label} · worp {selectedLandscapes[0]!.production_number}</strong>
                ) : <small>Kies dit landschap als eerste</small>}
              </div>
              <div className="scout-village-marker" aria-hidden="true"><span>◇</span><strong>Nieuw dorp</strong></div>
              <div className={`scout-placement-slot ${selectedLandscapes[1] ? "filled" : ""}`}>
                <span>Onder het nieuwe dorp</span>
                {selectedLandscapes[1] ? (
                  <strong><ResourceIcon resource={selectedLandscapes[1]!.resource} /> {resourceMeta[selectedLandscapes[1]!.resource].label} · worp {selectedLandscapes[1]!.production_number}</strong>
                ) : <small>Kies dit landschap als tweede</small>}
              </div>
              <button
                type="button"
                disabled={cards.length !== 2 || busy}
                onClick={() => setCards([cards[1], cards[0]])}
              >
                Boven en onder verwisselen
              </button>
            </section>
            <p className="scout-choice-instruction"><strong>Volledige landschapsstapel:</strong> je eerste keuze komt boven, je tweede onder. De gemarkeerde normale trekking mag dus gewoon blijven liggen.</p>
            <div className="landscape-picker">
              {kind.options.map((region) => {
                const meta = resourceMeta[region.resource];
                const selectedIndex = cards.indexOf(region.card_id);
                const currentIndex = (kind.current_card_ids || []).indexOf(region.card_id);
                const selectedPosition = selectedIndex === 0 ? "boven" : selectedIndex === 1 ? "onder" : undefined;
                const currentPosition = currentIndex === 0 ? "nu boven" : currentIndex === 1 ? "nu onder" : undefined;
                return (
                  <button
                    type="button"
                    key={region.card_id}
                    className={selectedIndex >= 0 ? "selected" : ""}
                    data-position={selectedPosition}
                    style={{ ["--resource" as string]: meta.color }}
                    aria-label={`${meta.label}, opbrengstworp ${region.production_number}${currentPosition ? `, normale trekking ${currentPosition}` : ""}${selectedPosition ? `, gekozen voor ${selectedPosition}` : ""}`}
                    onClick={() => toggleCard(region.card_id, kind.count)}
                  >
                    {selectedPosition ? <em>{selectedPosition === "boven" ? "1 · Boven" : "2 · Onder"}</em> : null}
                    <span>{region.production_number}</span>
                    <b><ResourceIcon resource={region.resource} /></b>
                    <small>{meta.label}</small>
                    {currentPosition ? <i>Normale trekking · {currentPosition}</i> : null}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}

        {kind.kind === "pay_gold_or_remove_unit" ? (
          <div className="choice-actions split-actions">
            <ResourceLocationPicker
              player={chooser}
              resources={Array(kind.gold).fill("goud" as Resource)}
              mode="payment"
              slots={payment.length === kind.gold ? payment : allocateResourceSlots(chooser, Array(kind.gold).fill("goud" as Resource), "payment")}
              onChange={setPayment}
              autoActivate
            />
            <button className="primary" disabled={busy || (payment.length === kind.gold ? payment : allocateResourceSlots(chooser, Array(kind.gold).fill("goud" as Resource), "payment")).some((slot) => !slot)} onClick={() => {
              const selected = payment.length === kind.gold ? payment : allocateResourceSlots(chooser, Array(kind.gold).fill("goud" as Resource), "payment");
              onResolve({ kind: "pay_gold" }, { payment: selected.filter(Boolean) as ResourceLocation[], storage: [] });
            }}>
              <span>Betaal</span><ResourceBadge resource="goud" amount={kind.gold} />
            </button>
            {kind.units.flatMap((id) => {
              const instance = revealed.get(id);
              const definition = instance ? definitions.get(instance.card_id) : undefined;
              return game.supply.decks.filter((deck) => deck.set === definition?.set).map((deck) => (
                <button disabled={busy} key={`${id}-${deck.deck_id}`} onClick={() => onResolve({ kind: "remove_unit", instance_id: id, deck_id: deck.deck_id })}>
                  {definition?.name || `Eenheid #${id}`} onderop stapel {deck.deck_id}
                </button>
              ));
            })}
          </div>
        ) : null}

        {kind.kind === "confirm" ? (
          <div className="choice-actions split-actions">
            <button disabled={busy} onClick={() => onResolve({ kind: "confirm", accepted: false })}>
              Nee
            </button>
            <button className={kind.danger ? "danger" : "primary"} disabled={busy} onClick={() => onResolve({ kind: "confirm", accepted: true })}>
              Ja, uitvoeren
            </button>
          </div>
        ) : null}

        {kind.kind !== "pay_gold_or_remove_unit" && kind.kind !== "confirm" ? (
          <div className="choice-actions">
            <span className="choice-summary">
              {kind.kind === "resources" || kind.kind === "same_resources"
                ? <><ResourceAmounts resources={resources} compact /><em>{resources.length} van {kind.count} gekozen</em></>
                : kind.kind === "resource_locations"
                  ? `${(kind.mode === "payment" ? payment : storage).filter(Boolean).length} van ${kind.resources.length} kaarten gekozen`
                : kind.kind === "cards" || kind.kind === "card_target"
                  ? `${cards.length} van ${kind.count} gekozen`
                  : kind.kind === "cards_to_deck"
                    ? `${cards.length} van ${kind.count} gekozen en geplaatst`
                  : kind.kind === "landscape_cards"
                    ? `${cards.length} van ${kind.count} landschappen gekozen`
                  : deck
                    ? `Stapel ${deck} gekozen · trekken gebeurt bovenop`
                    : "Kies een stapel"}
            </span>
            {cards.length || deck !== undefined ? (
              <button
                type="button"
                className="selection-clear"
                disabled={busy}
                onClick={() => {
                  setCards([]);
                  setDestinations({});
                  setDeck(undefined);
                }}
              >Selectie wissen</button>
            ) : null}
            <button
              className="primary"
              disabled={!canSubmit || busy}
              onClick={() => {
                if (kind.kind === "resources" || kind.kind === "same_resources") onResolve(
                  { kind: "resources", resources },
                  chooseTransferTypes ? undefined : {
                    payment: payment.filter(Boolean) as ResourceLocation[],
                    storage: storage.filter(Boolean) as ResourceLocation[],
                  },
                );
                else if (kind.kind === "resource_locations")
                  onResolve({
                    kind: "resource_locations",
                    locations: (kind.mode === "payment" ? payment : storage).filter(Boolean) as ResourceLocation[],
                  });
                else if (kind.kind === "cards" || kind.kind === "card_target")
                  onResolve({ kind: "cards", instance_ids: cards });
                else if (kind.kind === "cards_to_deck")
                  onResolve({ kind: "cards_to_deck", placements: cards.map((instance_id) => ({ instance_id, deck_id: destinations[instance_id] })) });
                else if (kind.kind === "landscape_cards")
                  onResolve({ kind: "landscape_cards", card_ids: cards });
                else if ((kind.kind === "starting_deck" || kind.kind === "draw_deck") && deck !== undefined)
                  onResolve({ kind: "deck", deck_id: deck, card_ids: cards });
              }}
            >
              {(kind.kind === "resources" && kind.allow_fewer && resources.length === 0)
                || ((kind.kind === "cards" || kind.kind === "card_target" || kind.kind === "cards_to_deck") && kind.allow_fewer && cards.length === 0)
                ? "Overslaan"
                : "Keuze bevestigen"}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function OrderedDeckCards({ deck, definitions, selected, onToggle, compact = false }: {
  deck: GameView["supply"]["decks"][number];
  definitions: Map<string, CardDef>;
  selected: number[];
  onToggle: (id: number) => void;
  compact?: boolean;
}) {
  const topFirst = [...(deck.searchable_cards || [])].reverse();
  return (
    <section className={`ordered-deck ${compact ? "compact" : ""}`} aria-label={`${deck.label}, van boven naar onder`}>
      <header>
        <span className="eyebrow">Stapel {deck.deck_id}</span>
        <h3>{deck.label}</h3>
        <p>De kaarten staan hieronder van <strong>bovenkant naar onderkant</strong>.</p>
      </header>
      <div className="card-picker ordered-deck-card-picker">
        {topFirst.map((instance, index) => {
          const position = deckPositionLabel(index, topFirst.length);
          const definition = definitions.get(instance.card_id);
          return (
            <div
              className={`ordered-deck-card ${index === 0 ? "is-top" : ""} ${index === topFirst.length - 1 ? "is-bottom" : ""}`}
              data-deck-position={index === 0 ? "top" : index === topFirst.length - 1 ? "bottom" : index + 1}
              role="group"
              aria-label={`${definition?.name || instance.card_id}: ${position}`}
              key={instance.instance_id}
            >
              <span className="deck-position-label">{position}</span>
              <GameCard
                instance={instance}
                definition={definition}
                selected={selected.includes(instance.instance_id)}
                onClick={() => onToggle(instance.instance_id)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
